import { useState } from 'react'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useSuppressProspect } from '../hooks/useProspects'
import type { Prospect, SuppressPayload } from '../api/prospectsApi'

/**
 * Suppression is terminal and there are TWO of them.
 *
 * The scope is an explicit choice with no default, and the copy spells out what each one
 * does, because "suppress" reads like one action and is not: publishing consent and
 * contact consent are different promises, and honouring the one someone did not ask for is
 * as wrong as honouring neither. A reason is required — a takedown with no recorded reason
 * is unreviewable later, and this is exactly the record that gets reviewed.
 */
export function SuppressDialog({
  prospect,
  open,
  onOpenChange,
}: {
  prospect: Prospect
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [scope, setScope] = useState<SuppressPayload['scope'] | null>(null)
  const [reason, setReason] = useState('')
  const suppress = useSuppressProspect()

  const SCOPES: Array<{ value: SuppressPayload['scope']; label: string; detail: string }> = [
    { value: 'contact', label: 'Stop contacting', detail: 'No further email. The page may still exist.' },
    { value: 'publishing', label: 'No page', detail: 'Never publish a page. We may still be able to write.' },
    { value: 'both', label: 'Both', detail: 'No page and no contact.' },
  ]

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Suppress {prospect.displayName || prospect.handle}</AlertDialogTitle>
          <AlertDialogDescription>
            This is terminal — it cannot be undone from this screen. Choose what is being
            withdrawn.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          {SCOPES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setScope(s.value)}
              className={`w-full rounded-md border p-3 text-left text-sm transition ${
                scope === s.value ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="font-medium">{s.label}</div>
              <div className="text-xs text-slate-500">{s.detail}</div>
            </button>
          ))}
        </div>

        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why? (recorded on the audit trail)"
          className="mt-2"
        />

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              disabled={!scope || reason.trim().length < 3 || suppress.isPending}
              onClick={() => {
                if (!scope) return
                suppress.mutate(
                  { idOrHandle: prospect.id, payload: { scope, reason: reason.trim() } },
                  { onSuccess: () => onOpenChange(false) },
                )
              }}
            >
              Suppress
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
